import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { useUserAuth } from '../context/UserAuthContext';
import {
  User,
  Mail,
  Building2,
  LogOut,
  Rocket,
  Settings,
  Bot,
  ChevronRight,
} from 'lucide-react';

const UserDashboard = () => {
  const { user, logout } = useUserAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-[#F6F7FB] pt-32 pb-20">
      <Helmet>
        <title>Dashboard | Liquicent</title>
      </Helmet>
      
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-[#0F172A] mb-1">
              Welcome back, {user?.full_name?.split(' ')[0]}!
            </h1>
            <p className="text-[#64748B]">Manage your agent deployments</p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              onClick={() => navigate('/user/settings')}
              variant="outline"
              className="border-[#E5E7EB] text-[#64748B] hover:bg-[#F6F7FB] hover:text-[#1A3A8F]"
            >
              <Settings className="w-4 h-4 mr-2" />
              Settings
            </Button>
            <Button
              onClick={handleLogout}
              variant="outline"
              className="border-[#E5E7EB] text-[#64748B] hover:bg-red-50 hover:text-red-600 hover:border-red-200"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Profile Card */}
          <Card 
            className="p-6 bg-white border border-[#E5E7EB] rounded-xl"
            style={{ boxShadow: '0px 6px 16px rgba(0,0,0,0.06)' }}
          >
            <h2 className="text-lg font-semibold text-[#0F172A] mb-6">Profile</h2>
            <div className="space-y-4">
              <div className="flex items-center gap-4 p-3 bg-[#F6F7FB] rounded-lg">
                <div className="w-10 h-10 rounded-lg bg-[#1A3A8F]/10 flex items-center justify-center">
                  <User className="w-5 h-5 text-[#1A3A8F]" />
                </div>
                <div>
                  <p className="text-xs text-[#64748B]">Name</p>
                  <p className="text-[#0F172A] font-medium">{user?.full_name}</p>
                </div>
              </div>
              <div className="flex items-center gap-4 p-3 bg-[#F6F7FB] rounded-lg">
                <div className="w-10 h-10 rounded-lg bg-[#1A3A8F]/10 flex items-center justify-center">
                  <Mail className="w-5 h-5 text-[#1A3A8F]" />
                </div>
                <div>
                  <p className="text-xs text-[#64748B]">Email</p>
                  <p className="text-[#0F172A] font-medium">{user?.email}</p>
                </div>
              </div>
              {user?.company && (
                <div className="flex items-center gap-4 p-3 bg-[#F6F7FB] rounded-lg">
                  <div className="w-10 h-10 rounded-lg bg-[#1A3A8F]/10 flex items-center justify-center">
                    <Building2 className="w-5 h-5 text-[#1A3A8F]" />
                  </div>
                  <div>
                    <p className="text-xs text-[#64748B]">Company</p>
                    <p className="text-[#0F172A] font-medium">{user.company}</p>
                  </div>
                </div>
              )}
            </div>
            <Button
              onClick={() => navigate('/user/settings')}
              variant="outline"
              className="w-full mt-6 border-[#E5E7EB] text-[#475569] hover:bg-[#F6F7FB] hover:text-[#1A3A8F]"
            >
              Edit Profile
            </Button>
          </Card>

          {/* Deployments Card */}
          <Card 
            className="lg:col-span-2 p-6 bg-white border border-[#E5E7EB] rounded-xl"
            style={{ boxShadow: '0px 6px 16px rgba(0,0,0,0.06)' }}
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-[#0F172A]">Your Deployments</h2>
              <span className="text-xs font-medium px-3 py-1 bg-[#F6F7FB] text-[#64748B] rounded-full">
                0 active
              </span>
            </div>
            
            <div className="text-center py-12">
              <div className="w-16 h-16 rounded-2xl bg-[#1A3A8F]/10 flex items-center justify-center mx-auto mb-4">
                <Rocket className="w-8 h-8 text-[#1A3A8F]" />
              </div>
              <h3 className="text-lg font-semibold text-[#0F172A] mb-2">No Deployments Yet</h3>
              <p className="text-[#64748B] mb-6 max-w-sm mx-auto">
                Browse our agent registry to find and deploy AI agents for your workflows.
              </p>
              <Button
                onClick={() => navigate('/agents')}
                className="bg-[#1A3A8F] hover:bg-[#142C6D] text-white"
              >
                <Bot className="w-4 h-4 mr-2" />
                Browse Agents
              </Button>
            </div>
          </Card>
        </div>

        {/* Quick Actions */}
        <div className="mt-8">
          <h2 className="text-lg font-semibold text-[#0F172A] mb-4">Quick Actions</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <QuickActionCard 
              title="Browse Agents"
              description="Explore our agent registry"
              icon={Bot}
              onClick={() => navigate('/agents')}
            />
            <QuickActionCard 
              title="View Workflows"
              description="See available workflows"
              icon={Rocket}
              onClick={() => navigate('/workflows/pr-governance')}
            />
            <QuickActionCard 
              title="Developer Docs"
              description="Build your own agents"
              icon={Settings}
              onClick={() => navigate('/developers')}
            />
            <QuickActionCard 
              title="Request Demo"
              description="Get a personalized demo"
              icon={User}
              onClick={() => navigate('/request-demo')}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const QuickActionCard = ({ title, description, icon: Icon, onClick }) => (
  <Card 
    className="p-4 bg-white border border-[#E5E7EB] rounded-xl hover:shadow-md hover:border-[#1A3A8F]/20 transition-all cursor-pointer group"
    onClick={onClick}
  >
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-[#F6F7FB] flex items-center justify-center group-hover:bg-[#1A3A8F]/10 transition-colors">
          <Icon className="w-5 h-5 text-[#64748B] group-hover:text-[#1A3A8F] transition-colors" />
        </div>
        <div>
          <p className="text-sm font-medium text-[#0F172A] group-hover:text-[#1A3A8F] transition-colors">{title}</p>
          <p className="text-xs text-[#64748B]">{description}</p>
        </div>
      </div>
      <ChevronRight className="w-4 h-4 text-[#94A3B8] group-hover:text-[#1A3A8F] group-hover:translate-x-1 transition-all" />
    </div>
  </Card>
);

export default UserDashboard;
